/**
 * Pure validation for a campaign's steps — no React, no fetch.
 *
 * Every key is shaped like the server's 422 `details[].path`
 * (`steps.0.variants.1.subject`, `steps.2.delayHours`, `steps`), so one
 * `errors` record can carry a client refusal and a server one and the editor
 * marks the same control for both.
 *
 * Indices are positions in the array the editor is handed, which is the array
 * `stepsToInput` sends — the server's index and ours must count into the same
 * list, or a refusal marks the wrong row.
 */

export const MAX_STEPS = 10;
export const SUBJECT_MAX = 255;
export const PREHEADER_MAX = 255;
export const MAX_DELAY_DAYS = 365;
export const MAX_DELAY_HOURS = 23;

export type StepKind = 'broadcast' | 'sequence';

/** The minimal shape the rules read — `StepDraft` satisfies it. */
export interface RuleStep {
  delayDays: number;
  delayHours: number;
  variants: readonly {
    label: 'A' | 'B';
    subject: string;
    preheader: string;
    templateId: number | null;
  }[];
}

export interface StepRuleOptions {
  kind: StepKind;
  abEnabled: boolean;
  /**
   * Also refuse an empty subject and a missing template — the checks the server
   * runs at SCHEDULE time. Off for an ordinary save, where a half-written draft
   * is a legitimate thing to keep.
   */
  requireComplete?: boolean;
}

/** Whether variant B is part of what gets sent — A/B is step 1 of a broadcast only. */
export function variantBApplies(kind: StepKind, abEnabled: boolean, stepIndex: number): boolean {
  return kind === 'broadcast' && abEnabled && stepIndex === 0;
}

export function stepPath(index: number, field: string): string {
  return `steps.${index}.${field}`;
}

export function variantPath(stepIndex: number, variantIndex: number, field: string): string {
  return `steps.${stepIndex}.variants.${variantIndex}.${field}`;
}

function isWholeNumber(n: number): boolean {
  return Number.isInteger(n) && n >= 0;
}

export function validateSteps(
  steps: readonly RuleStep[],
  { kind, abEnabled, requireComplete = false }: StepRuleOptions,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (steps.length === 0) {
    errors.steps = 'A campaign needs at least one email.';
    return errors;
  }
  if (kind === 'broadcast' && steps.length !== 1) {
    errors.steps = 'A broadcast sends exactly one email.';
  }
  if (steps.length > MAX_STEPS) {
    errors.steps = `A sequence holds at most ${MAX_STEPS} emails.`;
  }

  steps.forEach((step, i) => {
    const daysOk = isWholeNumber(step.delayDays) && step.delayDays <= MAX_DELAY_DAYS;
    const hoursOk = isWholeNumber(step.delayHours) && step.delayHours <= MAX_DELAY_HOURS;
    if (!daysOk) {
      errors[stepPath(i, 'delayDays')] = `Days must be a whole number from 0 to ${MAX_DELAY_DAYS}.`;
    }
    if (!hoursOk) {
      errors[stepPath(i, 'delayHours')] = `Hours must be a whole number from 0 to ${MAX_DELAY_HOURS}.`;
    }
    if (daysOk && hoursOk) {
      const total = step.delayDays * 24 + step.delayHours;
      if (i === 0 && total !== 0) {
        errors[stepPath(i, 'delayHours')] = 'The first email sends at the scheduled time, with no wait.';
      } else if (i > 0 && total < 1) {
        errors[stepPath(i, 'delayHours')] = 'Wait at least 1 hour after the previous email.';
      }
    }

    const withB = variantBApplies(kind, abEnabled, i);
    step.variants.forEach((variant, v) => {
      if (variant.label === 'B' && !withB) return;
      const subject = variant.subject.trim();
      if (subject.length > SUBJECT_MAX) {
        errors[variantPath(i, v, 'subject')] = `Keep the subject under ${SUBJECT_MAX} characters.`;
      } else if (requireComplete && subject === '') {
        errors[variantPath(i, v, 'subject')] = 'Enter a subject line.';
      }
      if (variant.preheader.trim().length > PREHEADER_MAX) {
        errors[variantPath(i, v, 'preheader')] = `Keep the preheader under ${PREHEADER_MAX} characters.`;
      }
      if (requireComplete && variant.templateId === null) {
        errors[variantPath(i, v, 'templateId')] = 'Choose a template.';
      }
    });

    if (withB && requireComplete && !step.variants.some((v) => v.label === 'B')) {
      errors[variantPath(i, 1, 'subject')] = 'Enter a subject line for variant B.';
    }
  });

  return errors;
}
